import { ScatterplotLayer, ScatterplotLayerProps } from "@deck.gl/layers"
import { CompositeLayer, PickingInfo } from "@deck.gl/core"
import { get } from "nodash"
import store from "integrations/redux/store"
import { customMapController, disabledMapController } from "features/map/control/slice"
import { LngLatCoordinatePair } from "features/map/event/types"
import { geometryDataStore } from "./GeometryDataStore"
import { GeometryMode } from "./GeometryLayer.class"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"
import { resolveDeckPickingLngLat } from "plugins/resolveLngLat"
import { Geometry } from "./types"

type GeometryLayerProps<T, DataType = [number, number]> = ScatterplotLayerProps<DataType> & {
    updatePoint: (coordinate: LngLatCoordinatePair, idx: number) => void
    pointStyles: Partial<ScatterplotLayerProps<T>>
    selectedPointStyles: Partial<ScatterplotLayerProps<T>>
    geometryName: string
    isLine: boolean
    canCloseLine: boolean
    closeLine: () => void
    closePoly: () => void
}

export default class GeometryPointsLayer<T extends Geometry = Geometry> extends CompositeLayer<
    GeometryLayerProps<T>
> {
    static layerName = "geometry_points"
    state = {} as {
        data: LngLatCoordinatePair[]
        original: LngLatCoordinatePair
        isUpdating: boolean
    }
    updateState({ changeFlags }: { changeFlags: Record<string, unknown> }) {
        const data = get(this.props, "data", {})
        const last = get(this.props, "last", {})
        if (changeFlags.dataChanged && !this.state.isUpdating) {
            this.setState({ data, last })
        }
    }

    onDragStart(info: PickingInfo) {
        const dispatch = store.dispatch
        dispatch(disabledMapController())
        const resolved = resolveDeckPickingLngLat(info)
        this.setState({
            original: [resolved?.longitude, resolved?.latitude] as LngLatCoordinatePair,
        })
    }

    onDrag(info: PickingInfo) {
        const { index } = info
        const resolved = resolveDeckPickingLngLat(info)
        if (!resolved) return false
        const lngLat = [resolved?.longitude, resolved?.latitude] as LngLatCoordinatePair
        geometryDataStore.updateShallow(lngLat, index)
    }

    onDragEnd(info: PickingInfo) {
        const { index } = info
        const resolved = resolveDeckPickingLngLat(info)
        const lngLat = [resolved?.longitude, resolved?.latitude] as LngLatCoordinatePair

        geometryDataStore.updatePoint(lngLat, index, this.state.original)
        const dispatch = store.dispatch
        dispatch(customMapController({ options: { dragRotate: false }, doubleClickZoom: false }))
    }

    tooltipHandler = (info: PickingInfo) => {
        if (
            this.props.canCloseLine &&
            info.index === this.state.data.length - 1 &&
            geometryDataStore.isLine() &&
            this.state.data.length > 1 &&
            geometryDataStore.mode === GeometryMode.LineToPolygon
        ) {
            return {
                html: `<div class="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                        <small>End ${this.props.geometryName}</small>
                    </div>`,
                style: {
                    background: "",
                },
            }
        }
        if (info.index === 0 && this.state.data.length > 2 && this.props.isLine) {
            return {
                html: `<div class="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                        <small>Enclose ${this.props.geometryName}</small>
                    </div>`,
                style: {
                    background: "",
                },
            }
        }
    }

    onClick(
        { index }: PickingInfo,
        event: {
            srcEvent: PointerEvent & { overPoint?: boolean; isPolygon?: boolean }
            rightButton: boolean
            leftButton: boolean
        }
    ) {
        event.srcEvent.overPoint = true

        if (!event.leftButton) return false

        const clickedFirstPoint = index === 0
        const shouldClosePolygon =
            clickedFirstPoint && this.state.data.length >= 3 && this.props.isLine

        const clickedLastPoint = index === this.state.data.length - 1
        const shouldEndLine =
            this.props.canCloseLine &&
            this.props.isLine &&
            clickedLastPoint &&
            this.state.data.length >= 2
        if (shouldEndLine && geometryDataStore.mode === GeometryMode.LineToPolygon) {
            this.props.closeLine()
        } else if (shouldClosePolygon) {
            this.props.closePoly()
        } else {
            geometryDataStore.setSelectedPointIndex(index)
        }
        return false
    }

    renderLayers() {
        return [
            new ScatterplotLayer({
                id: this.props.id + "all",
                data: this.state.data,
                filled: true,
                stroked: false,
                pickable: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getLineWidth: 2,
                getFillColor: (d: unknown) =>
                    d === this.state.data.at(-1) ? [113, 248, 127, 255] : [65, 141, 255, 255],
                getPosition: (d: [number, number]) => d,
                ...this.props.pointStyles,
                parameters: { depthTest: false },
                extensions: [...getGlobalTerrainData().extensions],
            }),
            new ScatterplotLayer({
                id: this.props.id + "selected",
                data:
                    geometryDataStore.selectedPointIndex !== -1
                        ? [this.state.data[geometryDataStore.selectedPointIndex]]
                        : [],
                stroked: true,
                filled: false,
                lineWidthMinPixels: 2,
                lineWidthMaxPixels: 2,
                radiusMinPixels: 11,
                radiusMaxPixels: 11,
                getPosition: (d: [number, number]) => {
                    return d
                },
                pickable: false,
                ...this.props.selectedPointStyles,
                parameters: { depthTest: false },
                extensions: [...getGlobalTerrainData().extensions],
            }),
        ]
    }
}
